import { Box, Typography } from "@mui/material";
import { PurchaseRow } from "@/pages/Purchased/component/PurchaseRow";
import { styles } from "./stylesHistoryTab";
import { useGetWalletHistoryQuery } from "@/store/api/walletApi";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import CustomSelect from "@/components/UI/CustomSelect/CustomSelect";
import ArrowsDiagram from "@/assets/svg/ArrowsDiagram";
import { PREMIUM_TIME_OPTIONS, TIME_OPTIONS, TimeRangeValue } from "@/types/timeRanges";

const industries = ["withdrawal", "deposit", "like", "zap"];
const LIMIT = 50;

const HistoryTab = () => {
  const [page, setPage] = useState(1);
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const { user: authUser, subscriptionStatus } = useAppSelector((state) => state.auth);
  const currentUid = authUser?.uid;
  const hasPremium = subscriptionStatus === "ACTIVE";

  const [timeRange, setTimeRange] = useState<TimeRangeValue>("24h");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  const selectedType = useMemo<("deposit" | "withdrawal" | "like" | "zap")[] | undefined>(() => {
    const allowed = ["deposit", "withdrawal", "like", "zap"] as const;
    const filtered = selectedIndustries.filter((v): v is (typeof allowed)[number] =>
      (allowed as readonly string[]).includes(v)
    );
    return filtered.length > 0 ? filtered : undefined;
  }, [selectedIndustries]);

  const queryArgs = useMemo(
    () => ({
      page,
      limit: LIMIT,
      time_range: timeRange || undefined,
      type: selectedType,
      order: sortOrder,
      _force: forceUpdate,
    }),
    [page, timeRange, selectedType, sortOrder, forceUpdate]
  );

  const { data, isFetching } = useGetWalletHistoryQuery(queryArgs, {
    skip: !currentUid,
    refetchOnMountOrArgChange: true,
  });

  const transactions = useMemo(() => data?.data ?? [], [data]);

  const txKey = (tx: any) =>
    tx.uid ?? `${tx.createdAt}-${tx.totalAmount}-${tx.sender?.uid ?? ""}`;

  useEffect(() => {
    if (!transactions) return;

    setAllTransactions((prev) => {
      if (page === 1) return transactions;

      const seen = new Set(prev.map(txKey));
      const newTxs = transactions.filter((tx) => !seen.has(txKey(tx)));
      return [...prev, ...newTxs];
    });

    setHasMore(transactions.length === LIMIT);
  }, [transactions, page]);

  const hasMoreRef = useRef(hasMore);
  const isFetchingRef = useRef(isFetching);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);

  const loadNextPage = useCallback(() => {
    if (isFetchingRef.current || !hasMoreRef.current) return;
    setPage((prev) => prev + 1);
  }, []);

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadNextPage();
      }
    });

    if (loadMoreRef.current) {
      observer.current.observe(loadMoreRef.current);
    }

    return () => observer.current?.disconnect();
  }, [loadNextPage]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [selectedIndustries, timeRange, sortOrder]);

  const cleanTitle = (note?: string | null, type?: string, network?: string) => {
    if (note) {
      return note.includes(":") ? note.split(":")[0].trim() : note.trim();
    }
    const normalizedType = String(type ?? "").toUpperCase();
    const formattedNetwork = network
      ? network.charAt(0).toUpperCase() + network.slice(1).toLowerCase()
      : "";
    if (normalizedType === "WITHDRAWAL")
      return formattedNetwork ? `Withdraw ${formattedNetwork}` : "Withdrawal";
    if (normalizedType === "DEPOSIT")
      return formattedNetwork ? `Deposit ${formattedNetwork}` : "Deposit";
    return type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : "Transaction";
  };

  const renderSelectedFilter = (selected: string[]) => {
    if (selected.length === 0) return "All";
    if (selected.length === 1) return selected[0];
    if (selected.length === industries.length) return "All";
    return `${selected.length} Selected`;
  };

  const handleSortClick = () => {
    setAllTransactions([]);
    setPage(1);
    setHasMore(true);
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const sortedTransactions = useMemo(() => {
    const toTime = (createdAt?: string) => new Date(createdAt ?? 0).getTime() || 0;
    return [...allTransactions].sort((a, b) =>
      sortOrder === "asc"
        ? toTime(a.createdAt) - toTime(b.createdAt)
        : toTime(b.createdAt) - toTime(a.createdAt)
    );
  }, [allTransactions, sortOrder]);

  const onChangeTimeRange = (labels: string[]) => {
    const label = labels[0] ?? "";
    const value =
      [...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS].find((o) => o.label === label)?.value ?? "";

    setAllTransactions([]);
    setPage(1);
    setHasMore(true);

    if (value === timeRange) {
      setForceUpdate((prev) => prev + 1);
    } else {
      setTimeRange(value);
    }
  };

  const purchases = sortedTransactions.map((tx) => {
    const type = String(tx.transactionType ?? "").toLowerCase();
    // Direction by transaction type first; deposits/withdrawals don't carry a
    // reliable sender.uid, so comparing it would mark everything as income (green).
    const isIncome =
      type === "withdrawal"
        ? false
        : type === "deposit"
          ? true
          : String(tx.sender?.uid) !== String(currentUid);
    const amount = tx.netAmount ?? 0;
    const fee = tx.fee ?? tx.platformFee ?? 0;
    return {
      key: txKey(tx),
      title: cleanTitle(tx.note, tx.transactionType, tx.payoutNetwork),
      date: new Date(tx.createdAt).toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      amount: amount,
      positive: isIncome,
      status: tx.status,
      relatedEntityId: tx.relatedEntityId,
      fee,
    };
  });

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.filtersRow}>
        <CustomSelect
          options={industries}
          selected={selectedIndustries}
          onChange={setSelectedIndustries}
          placeholder="All"
          checkbox
          multiple={true}
          width="454px"
          renderValue={renderSelectedFilter}
        />
        <CustomSelect
          options={[...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS].map((opt) => opt.label)}
          selected={
            timeRange
              ? [
                  [...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS].find((o) => o.value === timeRange)
                    ?.label ?? "",
                ]
              : []
          }
          onChange={onChangeTimeRange}
          placeholder="Select time range"
          width="454px"
          disabledOptions={!hasPremium ? PREMIUM_TIME_OPTIONS.map((o) => o.label) : []}
        />
        <Box
          sx={{
            ...styles.sortIcon,
            transform: sortOrder === "asc" ? "scaleY(-1)" : "none",
            transition: "transform 0.2s ease",
          }}
          onClick={handleSortClick}
          title={sortOrder === "desc" ? "Newest first" : "Oldest first"}
        >
          <ArrowsDiagram />
        </Box>
      </Box>

      <Box sx={styles.historyContainer} className="hide-scrollbar">
        {allTransactions.length === 0 && isFetching ? (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <CustomLoader size={50} />
          </Box>
        ) : allTransactions.length === 0 ? (
          <Box sx={styles.emptyBox}>
            <Typography sx={styles.emptyTitle}>No transactions yet</Typography>
            <Typography sx={styles.emptySubtitle}>
              Once you have completed your first transaction, you can view it here.
            </Typography>
          </Box>
        ) : (
          <>
            {purchases.map((item) => (
              <PurchaseRow
                key={item.key}
                title={item.title}
                date={item.date}
                amount={item.amount}
                positive={item.positive}
                status={item.status}
                relatedEntityId={item.relatedEntityId}
                fee={item.fee}
              />
            ))}

            <div ref={loadMoreRef} style={{ height: 1 }} />
            {isFetching && allTransactions.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CustomLoader size={30} />
              </Box>
            )}
            {!hasMore && purchases.length > 0 && !isFetching && (
              <Box sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}>
                No more transactions
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default HistoryTab;
